import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  api,
  type LayoutBinding,
  type LayoutTemplate,
  type LayoutZone,
} from '../api';
import { AppShell } from '../components/AppShell';

// Starter layouts the builder can clone. Kept small + self-contained;
// the display app has the richer built-ins, but these give users a
// sensible blank slate to drag from.
const STARTERS: Record<
  string,
  { name: string; zones: LayoutZone[]; bindings: Record<string, LayoutBinding> }
> = {
  blank: { name: 'Blank', zones: [], bindings: {} },
  simple: {
    name: 'Simple — Score + Clock',
    zones: [
      { id: 'home-name', type: 'info', x: 2, y: 4, w: 30, h: 10 },
      { id: 'period', type: 'clock', x: 35, y: 4, w: 30, h: 10 },
      { id: 'away-name', type: 'info', x: 68, y: 4, w: 30, h: 10 },
      { id: 'home-score', type: 'score', x: 2, y: 16, w: 30, h: 44 },
      { id: 'game-clock', type: 'clock', x: 35, y: 16, w: 30, h: 44 },
      { id: 'away-score', type: 'score', x: 68, y: 16, w: 30, h: 44 },
      { id: 'shot-clock', type: 'clock', x: 35, y: 62, w: 30, h: 24 },
      { id: 'home-fouls', type: 'info', x: 2, y: 62, w: 14, h: 24 },
      { id: 'away-fouls', type: 'info', x: 84, y: 62, w: 14, h: 24 },
    ],
    bindings: {
      'home-name': { kind: 'teamName', side: 'home' },
      period: { kind: 'period' },
      'away-name': { kind: 'teamName', side: 'away' },
      'home-score': { kind: 'score', side: 'home' },
      'game-clock': { kind: 'gameClock' },
      'away-score': { kind: 'score', side: 'away' },
      'shot-clock': { kind: 'shotClock' },
      'home-fouls': { kind: 'teamFouls', side: 'home' },
      'away-fouls': { kind: 'teamFouls', side: 'away' },
    },
  },
};

export function LayoutsPage() {
  const navigate = useNavigate();
  const [layouts, setLayouts] = useState<LayoutTemplate[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function load() {
    try {
      setErr(null);
      setLayouts(await api.listLayouts());
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'failed_to_load');
    }
  }
  useEffect(() => {
    load();
  }, []);

  async function createFrom(key: string) {
    const starter = STARTERS[key];
    setBusy(true);
    try {
      const created = await api.createLayout({
        name: starter.name === 'Blank' ? 'New layout' : starter.name,
        zones: starter.zones,
        bindings: starter.bindings,
      });
      navigate(`/layouts/${created.id}`);
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string) {
    if (!confirm('Delete this layout?')) return;
    await api.deleteLayout(id);
    await load();
  }

  return (
    <AppShell>
      <div className="space-y-6">
        <div className="flex items-end justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Scoreboard layouts</h1>
            <p className="mt-1 text-muted">
              Design custom display layouts, then assign them to a display.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              className="btn btn-secondary"
              onClick={() => createFrom('simple')}
              disabled={busy}
            >
              New from Simple
            </button>
            <button
              className="btn btn-primary"
              onClick={() => createFrom('blank')}
              disabled={busy}
            >
              + Blank layout
            </button>
          </div>
        </div>

        {err && <p className="card text-red-300">{err}</p>}

        {layouts.length === 0 ? (
          <p className="card text-muted">
            No saved layouts yet. Start from Simple or a blank canvas.
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
            {layouts.map((l) => (
              <div key={l.id} className="card flex flex-col gap-3">
                <MiniPreview zones={l.zones} />
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold">{l.name}</div>
                    <div className="text-xs text-muted">
                      {l.zones.length} zones · {l.aspectRatio}
                    </div>
                  </div>
                  <div className="flex gap-2 text-sm">
                    <Link
                      to={`/layouts/${l.id}`}
                      className="font-semibold text-brand-light hover:text-brand"
                    >
                      Edit
                    </Link>
                    <button
                      onClick={() => remove(l.id)}
                      className="text-red-300 hover:underline"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}

function MiniPreview({ zones }: { zones: LayoutZone[] }) {
  return (
    <div
      className="relative w-full overflow-hidden rounded-md border border-border bg-black"
      style={{ aspectRatio: '16 / 9' }}
    >
      {zones.map((z) => (
        <div
          key={z.id}
          className="absolute rounded-sm border border-brand/40 bg-brand/10"
          style={{
            left: `${z.x}%`,
            top: `${z.y}%`,
            width: `${z.w}%`,
            height: `${z.h}%`,
          }}
        />
      ))}
    </div>
  );
}
